/**
 * O que faz: Seção da ficha lateral do lead — rótulo curto em maiúsculas, ação opcional à direita e conteúdo.
 *            As seções são separadas por divisores de 1 px (princípio "linha antes de caixa" do design system),
 *            em vez de um cartão com borda e sombra para cada assunto.
 * Onde usa: LeadInfoSidebar (/chat) e, no modo semMoldura, LeadReturnSummary, HistoricoEtapasLead,
 *           LeadAttributionCard e OrigemAnuncioLead.
 * Conexões: classe .lg-rotulo de src/styles/gens-escopo.css.
 * Tabelas: Nenhuma.
 * Data: 07/10/2026
 */
import { useId, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SecaoFichaLeadProps {
  titulo: string;
  icone?: LucideIcon;
  /** Ação curta no canto direito (ex.: "Editar", atualizar). */
  acao?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function SecaoFichaLead({ titulo, icone: Icone, acao, children, className }: SecaoFichaLeadProps) {
  const tituloId = useId();

  return (
    <section aria-labelledby={tituloId} className={cn('min-w-0 py-4 first:pt-1 last:pb-1', className)}>
      <div className="mb-2 flex min-h-7 items-center justify-between gap-2">
        <h3 id={tituloId} className="lg-rotulo flex min-w-0 items-center gap-1.5">
          {Icone && <Icone className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
          <span className="truncate">{titulo}</span>
        </h3>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** Botão de texto pequeno usado como ação de seção ("Editar", "Adicionar"). */
export function AcaoSecaoFicha({
  children,
  onClick,
  rotuloAcessivel,
  ...resto
}: {
  children: ReactNode;
  onClick: () => void;
  rotuloAcessivel?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={rotuloAcessivel}
      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold text-accent-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      {...resto}
    >
      {children}
    </button>
  );
}
